import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Helmet from 'react-helmet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch, useSelector } from 'react-redux'
import { useAlert } from 'react-alert'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  createTaskLabelingCondition,
  createTaskMemberLabelingCondition,
  destroyTaskLabelingCondition,
  destroyTaskMemberLabelingCondition,
  fetchTaskCandidateServices,
  fetchTaskLabelingConditions,
  fetchTaskMemberLabelingConditions,
  updateTaskLabelingCondition,
  updateTaskMemberLabelingCondition,
} from '../../../../states/containers/task/taskSlice'
import {
  RequestGetTaskLabelingConditions,
  RequestGetTaskMemberLabelingConditions,
  ResponseGetTaskLabelingConditions,
  ResponseGetTaskMemberLabelingConditions,
} from '../../../../states/types/task'
import { updateTaskAutoLabelingSearchQuery } from '../../../../states/containers/common/currentSlice'
import { LangValue } from '../../../../states/types/common'
import { getGetPrams, setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import ConfirmationModal from '../../../components/task/ConfirmationModal'
import SearchConditionModule from '../../../components/task/SearchConditionModule'
import LabelingConditionModal, {
  LabelingConditionSubmit,
} from '../../../components/task/LabelingConditionModal'
import LabelingConditionContent, { LabelingConditionItem } from '../../../components/task/LabelingConditionContent'
import Tips from '../../../components/common/Tips'
import LoadingSquare from '../../../components/common/LoadingSquare'

type SettingMode = 'member' | 'project'

// プロフィール表示に使うユーザー情報のセット
type MemberProfileData = {
  id: number
  name: string
  profile_image: string | null
  code: string | null
  kana: string | null
}

type GetParams = {
  mode?: string
  team_ids?: string
  member_ids?: string
  label_group_ids?: string
  label_master_ids?: string
  condition_type?: string
  condition_keywords?: string
}

const LIMIT = 10

const NOT_ACTIVE_CONFIRMATION_MODAL_STATE = {
  display: false,
  type: "",
  name: "",
}

const Index: FC = () => {
  const {
    task,
    login,
    current: currentSetting,
  } = useSelector((state: RootState) => state)
  const dispatch = useDispatch()
  const alert = useAlert()
  const location = useLocation()
  const history = useHistory()
  const { language } = useParams<{ language: LangValue }>()

  const [currentSettingMode, setCurrentSettingMode] = useState<SettingMode>('member')
  // NOTE: モーダルが開いている間の 1 セッション分の設定。null = 閉じている。
  const [modalSession, setModalSession] = useState<{
    mode: 'create' | 'edit'
    variant: SettingMode
    initial?: LabelingConditionSubmit
    initialMember?: MemberProfileData
    onSubmit:(submitData: LabelingConditionSubmit) => void
      } | null>(null)

  const [pageOffset, setPageOffset] = useState<number>(1)
  const [isLoadingFirst, setIsLoadingFirst] = useState<boolean>(true)
  const [isLoadingList, setIsLoadingList] = useState<boolean>(false)
  const [isStop, setIsStop] = useState<boolean>(false)
  const [isDataLoad, setIsDataLoad] = useState<boolean>(false)

  const [filterLabelGroupIds, setFilterLabelGroupIds] = useState<number[]>([])
  const [filterLabelMasterIds, setFilterLabelMasterIds] = useState<number[]>([])
  const [filterConditionType, setFilterConditionType] = useState<string>("")
  const [filterConditionKeywords, setFilterConditionKeywords] = useState<string[]>([])
  const [filterTeamIds, setFilterTeamIds] = useState<number[]>([])
  const [filterMemberIds, setFilterMemberIds] = useState<number[]>([])
  const [candidateServices, setCandidateServices] = useState<{ id: number, name: string }[]>([])
  const [isCandidateServicesLoading, setIsCandidateServicesLoading] = useState<boolean>(true)

  // URL の初期読み込みが終わってから fetch を走らせるためのフラグ
  const [loadTrigger, setLoadTrigger] = useState<boolean>(false)

  const [confirmationModalState, setConfirmationModalState] = useState<{
    display: boolean
    type: string
    name: string
    callback?: VoidFunction
  }>(NOT_ACTIVE_CONFIRMATION_MODAL_STATE)

  const stopRef = useRef(isStop)
  const loadingListRef = useRef(isLoadingList)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (!stopRef.current && !loadingListRef.current && (bottomHeight() * adjustment) < (currentScroll() + windowHeight())) {
      setIsDataLoad(true)
    }
  }, [])

  // API

  const fetchConditions = (targetPage: number, onLoaded: (isEnd: boolean) => void): void => {
    if (currentSettingMode === 'member') {
      const params: RequestGetTaskMemberLabelingConditions = { page: targetPage, limit: LIMIT }
      if (filterTeamIds.length) params.team_ids = filterTeamIds
      if (filterMemberIds.length) params.member_ids = filterMemberIds
      if (filterLabelGroupIds.length) params.label_group_ids = filterLabelGroupIds
      if (filterLabelMasterIds.length) params.label_master_ids = filterLabelMasterIds
      if (filterConditionType) params.condition_type = filterConditionType
      if (filterConditionKeywords.length) params.keywords = filterConditionKeywords
      dispatch(fetchTaskMemberLabelingConditions(params, (result) => onLoaded(result.members.length === 0)))
    } else {
      const params: RequestGetTaskLabelingConditions = { page: targetPage, limit: LIMIT }
      if (filterLabelGroupIds.length) params.label_group_ids = filterLabelGroupIds
      if (filterLabelMasterIds.length) params.label_master_ids = filterLabelMasterIds
      if (filterConditionType) params.condition_type = filterConditionType
      if (filterConditionType === 'team_member') {
        if (filterTeamIds.length) params.team_ids = filterTeamIds
        if (filterMemberIds.length) params.member_ids = filterMemberIds
      } else if (filterConditionKeywords.length) {
        params.keywords = filterConditionKeywords
      }
      dispatch(fetchTaskLabelingConditions(params, (result) => onLoaded(result.label_groups.length === 0)))
    }
  }

  // handler

  const changeSettingMode = (nextMode: SettingMode): void => {
    if (nextMode === currentSettingMode) return
    setCurrentSettingMode(nextMode)
    setFilterLabelGroupIds([])
    setFilterLabelMasterIds([])
    setFilterConditionType("")
    setFilterConditionKeywords([])
    setFilterTeamIds([])
    setFilterMemberIds([])
    setPageOffset(1)
    setIsStop(false)
  }

  const requestDelete = (settingMode: SettingMode, id: number, name: string): void => {
    setConfirmationModalState({
      display: true,
      type: "labeling_condition_delete",
      name: name,
      callback: () => {
        if (settingMode === 'member') {
          dispatch(destroyTaskMemberLabelingCondition({ id }, () => {
            alert.success("自動仕分け条件を削除しました")
          }))
        } else {
          dispatch(destroyTaskLabelingCondition({ id }, () => {
            alert.success("自動仕分け条件を削除しました")
          }))
        }
      },
    })
  }

  const requestToggleEnabled = (settingMode: SettingMode, id: number, name: string, current: boolean): void => {
    const apply = () => {
      const payload = { id, is_disabled: !current }
      const enableMessage = !current ? "無効化" : "有効化"
      if (settingMode === 'member') {
        dispatch(updateTaskMemberLabelingCondition(payload, () => {
          alert.success(`自動仕分け条件を${enableMessage}しました`)
        }))
      } else {
        dispatch(updateTaskLabelingCondition(payload, () => {
          alert.success(`自動仕分け条件を${enableMessage}しました`)
        }))
      }
    }
    if (current) {
      apply()
      return
    }
    setConfirmationModalState({
      display: true,
      type: "labeling_condition_disable",
      name: name,
      callback: apply,
    })
  }

  const openCreateModal = (): void => {
    setModalSession({
      mode: 'create',
      variant: 'project',
      onSubmit: (submitData) => {
        dispatch(createTaskLabelingCondition({
          label_id: submitData.labelingTarget.labelId,
          company_service_group_ids: submitData.services.map((v) => v.id),
          keyword: { operator: submitData.keyword.operator ?? "or", words: submitData.keyword.words },
          file_paths: submitData.filePaths,
          url: { pattern: submitData.url.pattern ?? "partial", words: submitData.url.words },
          team_ids: submitData.teams.map((team) => team.id),
          member_ids: submitData.members.map((member) => member.id),
        }, () => {
          alert.success("自動仕分け条件を作成しました")
          setModalSession(null)
        }))
      },
    })
  }

  const openEditModal = (condition: LabelingConditionItem): void => {
    setModalSession({
      mode: 'edit',
      variant: 'project',
      initial: {
        labelingTarget: {
          labelGroupId: condition.labelGroup.id,
          labelGroupName: condition.labelGroup.name,
          labelGroupCode: condition.labelGroup.code,
          labelGroupColor: condition.labelGroup.color,
          labelId: condition.label.id,
          labelName: condition.label.name,
          labelCode: condition.label.code,
          labelColor: condition.label.color,
        },
        services: condition.services,
        keyword: condition.keyword,
        filePaths: condition.filePaths,
        url: condition.url,
        teams: condition.assignee.teams,
        members: condition.assignee.members,
      },
      onSubmit: (submitData) => {
        dispatch(updateTaskLabelingCondition({
          id: condition.id,
          label_id: submitData.labelingTarget.labelId,
          company_service_group_ids: submitData.services.map((v) => v.id),
          keyword: { operator: submitData.keyword.operator ?? "or", words: submitData.keyword.words },
          file_paths: submitData.filePaths,
          url: { pattern: submitData.url.pattern ?? "partial", words: submitData.url.words },
          team_ids: submitData.teams.map((team) => team.id),
          member_ids: submitData.members.map((member) => member.id),
        }, () => {
          alert.success("自動仕分け条件の編集をしました")
          setModalSession(null)
        }))
      },
    })
  }

  const openMemberCreateModal = (member?: MemberProfileData): void => {
    setModalSession({
      mode: 'create',
      variant: 'member',
      initialMember: member,
      onSubmit: (submitData) => {
        if (!submitData.member) return
        dispatch(createTaskMemberLabelingCondition({
          apply_status_dates: [],
          member_labeling_condition: {
            member_id: submitData.member.id,
            label_id: submitData.labelingTarget.labelId,
            company_service_group_ids: submitData.services.map((v) => v.id),
            keyword: { operator: submitData.keyword.operator ?? "or", words: submitData.keyword.words },
            file_paths: submitData.filePaths,
            url: { pattern: submitData.url.pattern ?? "partial", words: submitData.url.words },
          },
        }, () => {
          alert.success("自動仕分け条件を作成しました")
          setModalSession(null)
        }))
      },
    })
  }

  const openMemberEditModal = (condition: LabelingConditionItem, member: MemberProfileData): void => {
    setModalSession({
      mode: 'edit',
      variant: 'member',
      initialMember: member,
      initial: {
        labelingTarget: {
          labelGroupId: condition.labelGroup.id,
          labelGroupName: condition.labelGroup.name,
          labelGroupCode: condition.labelGroup.code,
          labelGroupColor: condition.labelGroup.color,
          labelId: condition.label.id,
          labelName: condition.label.name,
          labelCode: condition.label.code,
          labelColor: condition.label.color,
        },
        services: condition.services,
        keyword: condition.keyword,
        filePaths: condition.filePaths,
        url: condition.url,
        teams: [],
        members: [],
      },
      onSubmit: (submitData) => {
        if (!submitData.member) return
        dispatch(updateTaskMemberLabelingCondition({
          id: condition.id,
          member_id: submitData.member.id,
          label_id: submitData.labelingTarget.labelId,
          company_service_group_ids: submitData.services.map((v) => v.id),
          keyword: { operator: submitData.keyword.operator ?? "or", words: submitData.keyword.words },
          file_paths: submitData.filePaths,
          url: { pattern: submitData.url.pattern ?? "partial", words: submitData.url.words },
        }, () => {
          alert.success("自動仕分け条件の編集をしました")
          setModalSession(null)
        }))
      },
    })
  }

  const projectData: ResponseGetTaskLabelingConditions = task.taskLabelingConditions ?? {
    importing_project: false,
    label_groups: [],
    candidate_members: [],
    candidate_teams: [],
    candidate_label_groups: [],
    selected_label_groups: [],
    candidate_label_masters: [],
  }
  const memberData: ResponseGetTaskMemberLabelingConditions = task.taskMemberLabelingConditions ?? {
    members: [],
    candidate_teams: [],
    candidate_members: [],
    candidate_label_groups: [],
    selected_label_groups: [],
    candidate_label_masters: [],
  }

  const labelGroupList = useMemo(() => {
    if (currentSettingMode === 'member') {
      return memberData.candidate_label_groups.map((g) => ({ id: g.id, name: g.name }))
    }
    return projectData.candidate_label_groups.map((g) => ({ id: g.label_group_id, name: g.label_group_name }))
  }, [currentSettingMode, memberData, projectData])

  const selectedLabelGroupList = useMemo(() => {
    const data = currentSettingMode === 'member' ? memberData : projectData
    return data.selected_label_groups.map((g) => ({ id: g.id, name: g.name }))
  }, [currentSettingMode, memberData, projectData])

  const candidateLabelings = useMemo(() => projectData.candidate_label_groups.map((labelGroup) => ({
    labelGroupId: labelGroup.label_group_id,
    labelGroupName: labelGroup.label_group_name,
    labelGroupCode: labelGroup.label_group_code,
    labelGroupColor: labelGroup.label_group_color,
    disabled: labelGroup.disabled,
    labels: labelGroup.labels.map((label) => ({
      labelId: label.label_id,
      labelName: label.label_name,
      labelCode: label.label_code,
      labelColor: label.label_color,
      description: label.description,
      disabled: label.disabled,
    })),
  })), [projectData])

  const labelMasterList = useMemo(() => {
    const candidates = currentSettingMode === 'member'
      ? memberData.candidate_label_masters
      : projectData.candidate_label_masters
    return candidates.map((m) => ({ id: m.id, name: m.name }))
  }, [currentSettingMode, memberData, projectData])

  const teamList = useMemo(() => {
    const candidates = currentSettingMode === 'member'
      ? memberData.candidate_teams
      : projectData.candidate_teams
    return candidates.map((t) => ({ id: t.id, name: t.name }))
  }, [currentSettingMode, memberData, projectData])

  const memberFilterList = useMemo(() => {
    const candidates = currentSettingMode === 'member'
      ? memberData.candidate_members
      : projectData.candidate_members
    return candidates.map((m) => ({ id: m.id, name: m.name }))
  }, [currentSettingMode, memberData, projectData])

  useEffect(() => {
    let getParams = getGetPrams(location.search) as GetParams
    if (!location.search && currentSetting.taskAutoLabelingSearchQuery) {
      getParams = getGetPrams(currentSetting.taskAutoLabelingSearchQuery) as GetParams
    }

    if (getParams.mode === 'project' || getParams.mode === 'member') {
      setCurrentSettingMode(getParams.mode)
    }
    if (getParams.team_ids) setFilterTeamIds(getParams.team_ids.split(',').map(Number))
    if (getParams.member_ids) setFilterMemberIds(getParams.member_ids.split(',').map(Number))
    if (getParams.label_group_ids) setFilterLabelGroupIds(getParams.label_group_ids.split(',').map(Number))
    if (getParams.label_master_ids) setFilterLabelMasterIds(getParams.label_master_ids.split(',').map(Number))
    if (getParams.condition_type) setFilterConditionType(getParams.condition_type)
    if (getParams.condition_keywords) setFilterConditionKeywords(decodeURIComponent(getParams.condition_keywords).split(','))
    setLoadTrigger(true)
  }, [])

  useEffect(() => {
    if (!loadTrigger || !login.accessToken) return
    if (currentSettingMode === 'project' && !login.feature_config?.task_project_editable) {
      changeSettingMode('member')
      return
    }
    const query: string[] = [`mode=${currentSettingMode}`]
    if (filterLabelGroupIds.length) query.push(`label_group_ids=${filterLabelGroupIds.join(',')}`)
    if (filterLabelMasterIds.length) query.push(`label_master_ids=${filterLabelMasterIds.join(',')}`)
    if (filterConditionType) query.push(`condition_type=${filterConditionType}`)
    if (filterConditionKeywords.length) query.push(`condition_keywords=${filterConditionKeywords.join(',')}`)
    if (filterTeamIds.length) query.push(`team_ids=${filterTeamIds.join(',')}`)
    if (filterMemberIds.length) query.push(`member_ids=${filterMemberIds.join(',')}`)

    setIsLoadingFirst(true)
    setIsStop(false)
    setPageOffset(1)
    fetchConditions(1, (isEnd) => {
      setIsLoadingFirst(false)
      setIsDataLoad(false)
      if (isEnd) setIsStop(true)
    })
    history.push(`${setPath(URL.TASK_AUTO_LABELING.path, language)}?${query.join('&')}`)
  }, [
    currentSettingMode,
    filterLabelGroupIds,
    filterLabelMasterIds,
    filterConditionType,
    filterConditionKeywords,
    filterTeamIds,
    filterMemberIds,
    loadTrigger,
    login.accessToken,
  ])

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(
      fetchTaskCandidateServices(
        (result) => {
          setCandidateServices(result.candidate_services)
          setIsCandidateServicesLoading(false)
        },
      ),
    )
  }, [login.accessToken])

  useEffect(() => {
    if (!location.search) return
    dispatch(updateTaskAutoLabelingSearchQuery(location.search))
  }, [location.search])

  useEffect(() => {
    if (!loadTrigger || pageOffset === 1) {
      setIsDataLoad(false)
      setIsLoadingList(false)
      return
    }
    fetchConditions(pageOffset, (isEnd) => {
      setIsDataLoad(false)
      setIsLoadingList(false)
      if (isEnd) setIsStop(true)
    })
  }, [pageOffset])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  useEffect(() => {
    if (isLoadingFirst || isStop || !isDataLoad) {
      setIsDataLoad(false)
      return
    }
    setPageOffset(pageOffset + 1)
    setIsLoadingList(true)
  }, [isDataLoad])

  useEffect(() => {
    stopRef.current = isStop
  }, [isStop])

  useEffect(() => {
    loadingListRef.current = isLoadingList
  }, [isLoadingList])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 自動仕分け設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        自動仕分け設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          自動仕分け設定
        </h1>
      </section>
      <section className="page_description">
        <p>
          自動仕分け設定の表示、編集、削除をすることができます。
        </p>
        <p>
          条件に合致する業務データが自動で仕分けられるようになります。
        </p>
        <p>
          プロジェクト・カテゴリー設定権限が付与された場合、プロジェクト毎の自動仕分け設定ができます。
        </p>
      </section>
      <section className="labeling_condition_type_select_wrapper">
        表示する自動仕分け設定の切り替え：
        <button
          type="button"
          style={{ backgroundColor: "#a36bbd" }}
          onClick={() => changeSettingMode('member')}
        >
          ユーザー個別の設定
          {
            currentSettingMode === 'member' && (
              <div className="pick_up">
                表示中
              </div>
            )
          }
        </button>
        {
          !login.feature_config?.task_project_editable ? (
            <Tips
              description="プロジェクト編集権限が付与された場合のみ表示できます"
              className=""
              icon={false}
            >
              <button
                type="button"
                style={{ backgroundColor: "#3773bc" }}
                disabled
              >
                プロジェクト毎の設定
              </button>
            </Tips>
          ) : (
            <button
              type="button"
              style={{ backgroundColor: "#3773bc" }}
              onClick={() => changeSettingMode('project')}
            >
              プロジェクト毎の設定
              {
                currentSettingMode === 'project' && (
                  <div className="pick_up">
                    表示中
                  </div>
                )
              }
            </button>
          )
        }

      </section>
      <SearchConditionModule
        name="自動仕分け設定"
        mode={currentSettingMode}
        labelGroupIds={filterLabelGroupIds}
        callbackLabelGroupIds={setFilterLabelGroupIds}
        labelGroupList={labelGroupList}
        labelGroupIncrementalSearch={login.feature_config?.high_volume}
        labelGroupSearchMemberScope={currentSettingMode === 'member'}
        selectedLabelGroupList={selectedLabelGroupList}
        labelMasterIds={filterLabelMasterIds}
        callbackLabelMasterIds={setFilterLabelMasterIds}
        labelMasterList={labelMasterList}
        conditionType={filterConditionType}
        callbackConditionType={setFilterConditionType}
        conditionKeywords={filterConditionKeywords}
        callbackConditionKeywords={setFilterConditionKeywords}
        teamIds={filterTeamIds}
        callbackTeamIds={setFilterTeamIds}
        teamList={teamList}
        memberIds={filterMemberIds}
        callbackMemberIds={setFilterMemberIds}
        memberList={memberFilterList}
      />
      <section className="page_contents content-area task_auto_labeling">
        <div className="content-block">
          <div className="block-header">
            <div className="button-area" style={{ display: "flex", justifyContent: "flex-end" }}>
              {
                currentSettingMode === 'member' ? (
                  <button type="button" className="button" onClick={() => openMemberCreateModal()}>
                    <FontAwesomeIcon icon={['fas', 'plus']} style={{ marginRight: "4px" }} />
                    ユーザー毎の自動仕分け設定を作成
                  </button>
                ) : (
                  <button type="button" className="button" onClick={openCreateModal}>
                    <FontAwesomeIcon icon={['fas', 'plus']} style={{ marginRight: "4px" }} />
                    プロジェクト毎の自動仕分け設定を作成する
                  </button>
                )
              }
            </div>
          </div>
          <div className="block-detail">
            {
              isLoadingFirst ? (
                <LoadingSquare>
                  <>
                    自動仕分け設定を読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  {
                    currentSettingMode === 'member' ? (
                      <MemberAutoLabelingContent
                        data={memberData}
                        onDeleteCallback={(id, name) => requestDelete('member', id, name)}
                        onToggleEnabledCallback={(id, name, current) => requestToggleEnabled('member', id, name, current)}
                        onEditCallback={openMemberEditModal}
                        onAddCallback={openMemberCreateModal}
                      />
                    ) : (
                      <ProjectAutoLabelingContent
                        data={projectData}
                        onDeleteCallback={(id, name) => requestDelete('project', id, name)}
                        onToggleEnabledCallback={(id, name, current) => requestToggleEnabled('project', id, name, current)}
                        onEditCallback={openEditModal}
                        onAddCallback={openCreateModal}
                      />
                    )
                  }
                  {
                    isLoadingList && (
                      <div className="block-detail">
                        <LoadingSquare>
                          <>
                            自動仕分け設定を読み込んでいます…
                          </>
                        </LoadingSquare>
                      </div>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </section>
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={() => setConfirmationModalState(NOT_ACTIVE_CONFIRMATION_MODAL_STATE)}
      />
      <LabelingConditionModal
        isDisplay={modalSession !== null}
        mode={modalSession?.mode ?? 'create'}
        variant={modalSession?.variant ?? 'project'}
        modalState={{
          initialLabelingCondition: modalSession?.initial,
          candidateMembers: projectData.candidate_members,
          candidateTeams: projectData.candidate_teams,
          candidateServices,
          isCandidateServicesLoading,
          candidateLabelings,
          candidateSelectableMembers: memberData.candidate_members.map((member) => ({
            id: member.id,
            name: member.name,
            profile_image: member.profile_image,
            code: member.code,
            kana: member.kana,
          })),
          initialMember: modalSession?.initialMember,
        }}
        onCancelCallback={() => setModalSession(null)}
        onSubmitCallback={(submitData) => modalSession?.onSubmit(submitData)}
      />
    </section>
  )
}

const MemberAutoLabelingContent: FC<{
  data: ResponseGetTaskMemberLabelingConditions
  onToggleEnabledCallback: (id: number, name: string, current: boolean) => void
  onDeleteCallback: (id: number, name: string) => void
  onEditCallback: (condition: LabelingConditionItem, member: MemberProfileData) => void
  onAddCallback: (member: MemberProfileData) => void
}> = (props) => {
  const {
    data,
    onToggleEnabledCallback,
    onDeleteCallback,
    onEditCallback,
    onAddCallback,
  } = props
  return (
    <>
      {
        data.members.length > 0 ? (
          <>
            {data.members.map((member) => (
              <LabelingConditionContent
                key={member.id}
                defaultOpen={member.is_self}
                labelingConditions={member.member_labeling_conditions.map((condition, index) => ({
                  id: condition.id,
                  number: index + 1,
                  disabledAt: condition.disabled_at,
                  services: condition.services,
                  keyword: condition.keyword,
                  filePaths: condition.file_paths,
                  url: condition.url,
                  assignee: { members: [], teams: [] },
                  label: condition.label,
                  labelGroup: condition.label_group,
                }))}
                onDeleteCallback={(id, name) => onDeleteCallback(id, name)}
                onToggleEnabledCallback={(id, name, current) => onToggleEnabledCallback(id, name, current)}
                onEditCallback={(condition) => onEditCallback(condition, {
                  id: member.id,
                  name: member.name,
                  profile_image: member.profile_image,
                  code: member.code,
                  kana: member.kana,
                })}
                onAddCallback={() => onAddCallback({
                  id: member.id,
                  name: member.name,
                  profile_image: member.profile_image,
                  code: member.code,
                  kana: member.kana,
                })}
              >
                <div className="member_profile">
                  {
                    member.profile_image ? (
                      <img alt="profile" src={member.profile_image} className="profile_icon" />
                    ) : (
                      <img
                        alt=""
                        src={`/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                        className="profile_icon"
                      />
                    )
                  }
                  <ruby>
                    {member.name}
                    <rt>{member.kana}</rt>
                  </ruby>
                  {
                    member.code && (
                      <>
                        {`(${member.code})`}
                      </>
                    )
                  }
                </div>
              </LabelingConditionContent>
            ))}
          </>
        ) : (
          <div className="no_data">データがありません</div>
        )
      }
    </>
  )
}

const ProjectAutoLabelingContent: FC<{
  data: ResponseGetTaskLabelingConditions
  onToggleEnabledCallback: (id: number, name: string, current: boolean) => void
  onDeleteCallback: (id: number, name: string) => void
  onEditCallback: (condition: LabelingConditionItem) => void
  onAddCallback: () => void
}> = (props) => {
  const {
    data,
    onToggleEnabledCallback,
    onDeleteCallback,
    onEditCallback,
    onAddCallback,
  } = props
  return (
    <>
      {
        data.label_groups.length > 0 ? (
          <>
            {data.label_groups.map((labelGroup) => (
              <LabelingConditionContent
                key={labelGroup.id}
                labelingConditions={labelGroup.labeling_conditions.map((condition, index) => ({
                  id: condition.id,
                  number: index + 1,
                  disabledAt: condition.disabled_at,
                  services: condition.services,
                  keyword: condition.keyword,
                  filePaths: condition.file_paths,
                  url: condition.url,
                  assignee: { members: condition.members, teams: condition.teams },
                  label: condition.label,
                  labelGroup: labelGroup,
                }))}
                onDeleteCallback={(id, name) => onDeleteCallback(id, name)}
                onToggleEnabledCallback={(id, name, current) => onToggleEnabledCallback(id, name, current)}
                onEditCallback={onEditCallback}
                onAddCallback={onAddCallback}
              >
                <span className="label_group_header">
                  <span className="label_group_color" style={{ backgroundColor: labelGroup.color }} />
                  {labelGroup.name}
                </span>
              </LabelingConditionContent>
            ))}
          </>
        ) : (
          <div className="no_data">データがありません</div>
        )
      }
    </>
  )
}

export default Index
